import React, { FunctionComponent, useMemo } from 'react';
import cn from 'classnames';
import LazyImage from '../LazyImage';
import LazyImageResponsive from '../LazyImage/LazyImageResponsive';
import getImgxUrl from '../LazyImage/getImgxUrl';
import { AVATAR } from './classnames';
import { AvatarProps } from './types';

const Avatar: FunctionComponent<AvatarProps> = ({
  src: srcProp,
  placeholder,
  bgColor = 'gray-lighter',
  alt = 'avatar',
  observerProps,
  imgClassName: imgClassNameProp,
  className,
  size = 40,
  href,
  aProps,
  isLazy = true,
  ...other
}) => {
  const { src, responsiveSrc, sizeClasses } = useMemo(() => {
    if (typeof size === 'number') {
      return {
        src: srcProp
          ? getImgxUrl(srcProp, { w: size, h: size, crop: 'faces', dpr: 2 })
          : undefined,
        sizeClasses: cn(`w-${size}`, `h-${size}`),
      };
    }
    let defaultSrc = '';
    let sm = '';
    let md = '';
    let lg = '';
    let sources = { default: defaultSrc, sm, md, lg };

    if (srcProp) {
      defaultSrc = getImgxUrl(srcProp, {
        w: size.xs,
        h: size.xs,
        crop: 'faces',
        dpr: 2,
      });
      sm = size.sm
        ? getImgxUrl(srcProp, { w: size.sm, h: size.sm, crop: 'faces', dpr: 2 })
        : defaultSrc;
      md = size.md
        ? getImgxUrl(srcProp, { w: size.md, h: size.md, crop: 'faces', dpr: 2 })
        : sm;
      lg = size.lg
        ? getImgxUrl(srcProp, { w: size.lg, h: size.lg, crop: 'faces', dpr: 2 })
        : md;

      sources = { default: defaultSrc, sm, md, lg };
    }

    let classes = cn(`w-${size.xs}`, `h-${size.xs}`);
    let prevSize = size.xs;

    if (size.sm && size.sm !== prevSize) {
      classes = cn(classes, `sm:w-${size.sm}`, `sm:h-${size.sm}`);
      prevSize = size.sm;
    }
    if (size.md && size.md !== prevSize) {
      classes = cn(classes, `md:w-${size.md}`, `md:h-${size.md}`);
      prevSize = size.md;
    }
    if (size.lg && size.lg !== prevSize) {
      classes = cn(classes, `lg:w-${size.lg}`, `lg:h-${size.lg}`);
    }
    return { responsiveSrc: sources, src: lg, sizeClasses: classes };
  }, [srcProp, size]);

  const imgClassName = cn('w-full h-full', `bg-${bgColor}`, imgClassNameProp);

  const emptySource = <div className={imgClassName} />;

  const responsiveLazy = responsiveSrc ? (
    <LazyImageResponsive
      className={imgClassName}
      imgClassName="object-cover"
      placeholder={placeholder}
      sources={responsiveSrc}
      alt={alt}
      observerProps={observerProps}
    />
  ) : (
    emptySource
  );

  const simpleLazy = src ? (
    <LazyImage
      className={imgClassName}
      imgClassName="object-cover"
      placeholder={placeholder}
      src={src}
      alt={alt}
      observerProps={observerProps}
    />
  ) : (
    emptySource
  );

  const lazy = responsiveSrc ? responsiveLazy : simpleLazy;

  const img = src ? (
    <img
      className={imgClassName}
      style={{ objectFit: 'cover' }}
      src={src}
      alt={alt}
    />
  ) : (
    emptySource
  );

  if (href) {
    return (
      <a
        className={cn(AVATAR, sizeClasses, className)}
        href={href}
        {...aProps}
        {...(other as React.HTMLAttributes<HTMLAnchorElement>)}
      >
        {isLazy ? lazy : img}
      </a>
    );
  }

  return (
    <div className={cn(AVATAR, sizeClasses, className)} {...other}>
      {isLazy ? lazy : img}
    </div>
  );
};

export default Avatar;
